import clsx from 'clsx';
import React, { HTMLAttributes, ReactNode, Ref } from 'react';

import { Icon } from '~/components/icon/Icon';
import { SvgIconNames } from '~/design-tokens/iconography/SvgIcons';

import styles from './Badge.module.scss';

export type BadgeSize = 'small' | 'medium';
export type BadgeStyle = 'solid' | 'outline';
export type BadgeVariant = 'textual' | 'numerical';
export type BadgeColor =
  | 'neutral'
  | 'primary'
  | 'error'
  | 'success'
  | 'warning';

export interface BadgeProps extends HTMLAttributes<HTMLDivElement> {
  variant?: BadgeVariant;
  size?: BadgeSize;
  color?: BadgeColor;
  badgeStyle?: BadgeStyle;
  label?: ReactNode;
  leftIcon?: SvgIconNames;
  rightIcon?: SvgIconNames;
  leftSection?: React.ReactNode;
  ref?: Ref<HTMLDivElement>;
}

const textualStyleMap: Record<BadgeStyle, Record<BadgeColor, string>> = {
  solid: {
    neutral: styles.solidNeutral,
    primary: styles.solidPrimary,
    success: styles.solidSuccess,
    warning: styles.solidWarning,
    error: styles.solidError,
  },
  outline: {
    neutral: styles.outlineNeutral,
    primary: styles.outlinePrimary,
    success: styles.outlineSuccess,
    warning: styles.outlineWarning,
    error: styles.outlineError,
  },
};

export const Badge = ({
  color = 'primary',
  size = 'medium',
  badgeStyle = 'solid',
  variant = 'textual',
  label,
  leftIcon,
  rightIcon,
  leftSection,
  className,
  ref,
  ...rest
}: BadgeProps) => {
  const sizeClass = size === 'small' ? styles.sizeSmall : styles.sizeMedium;
  const rootClass = clsx(
    styles.root,
    size === 'small' && styles.sizeSmall,
    className
  );

  if (variant === 'numerical') {
    return (
      <div
        ref={ref}
        data-testid="badge-numerical"
        className={rootClass}
        {...rest}
      >
        <p
          data-badge="numerical"
          className={clsx(styles.numerical, sizeClass)}
        >
          {label}
        </p>
      </div>
    );
  }

  const iconSize = size === 'small' ? 12 : 16;

  return (
    <div ref={ref} data-testid="badge-textual" className={rootClass} {...rest}>
      <div
        data-badge="textual"
        className={clsx(
          styles.textual,
          sizeClass,
          textualStyleMap[badgeStyle][color]
        )}
      >
        {leftSection}
        {leftIcon && <Icon iconName={leftIcon} size={iconSize} />}
        <span>{label}</span>
        {rightIcon && <Icon iconName={rightIcon} size={iconSize} />}
      </div>
    </div>
  );
};
